import Api from '@/api';
import { useQuery, useMutation, useQueryClient, QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { PreferencesType } from './types';
import { useToast } from '@/context/toast/ToastContext';
import { useProfile } from '@/modules/profile/queries';

export const PREFERENCES_QUERY_KEY = ['preferences'];

export const usePreferenceList = (
  options: Omit<UseQueryOptions<PreferencesType, Error, PreferencesType, QueryKey>, 'queryKey' | 'queryFn'> = {},
) => {
  const { isSuccess: isProfileLoaded } = useProfile();

  return useQuery({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: () => Api.getPreferences(),
    enabled: isProfileLoaded,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
};

export const useUpdatePreferences = () => {
  const queryClient = useQueryClient();
  const { handleToastOpen } = useToast();

  return useMutation({
    mutationFn: async (payload: PreferencesType) => {
      return Api.updatePreferences(payload) as Promise<PreferencesType>;
    },

    onMutate: async (partialPayload) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCES_QUERY_KEY });
      const previousPreferences = queryClient.getQueryData<PreferencesType>(PREFERENCES_QUERY_KEY);

      if (previousPreferences) {
        queryClient.setQueryData<PreferencesType>(PREFERENCES_QUERY_KEY, {
          ...previousPreferences,
          ...partialPayload,
        });
      }
      return { previousPreferences };
    },

    onError: (err, newPreferences, context) => {
      if (context?.previousPreferences) {
        queryClient.setQueryData(PREFERENCES_QUERY_KEY, context.previousPreferences);
      }
      handleToastOpen({
        type: 'error',
        title: `${err.message}. Couldn't update preferences. Please try again`,
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PREFERENCES_QUERY_KEY });
    },
  });
};
